import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useInterviews(applicationId?: string) {
  const queryClient = useQueryClient();

  const { data: interviews = [], isLoading, refetch } = useQuery({
    queryKey: ["interviews", applicationId],
    queryFn: async () => {
      let query = supabase
        .from("interviews")
        .select(`
          *,
          application:job_applications!inner(
            id,
            job:jobs!inner(
              id,
              title,
              company_name
            ),
            profile:profiles!job_applications_job_seeker_id_fkey(
              id,
              full_name,
              email,
              avatar_url
            )
          )
        `)
        .order("scheduled_at", { ascending: true });

      if (applicationId) {
        query = query.eq("application_id", applicationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const updateInterview = useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Record<string, any> 
    }) => {
      const { error } = await supabase
        .from("interviews")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interviews"] });
      toast({ title: "Interview updated successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update interview",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const cancelInterview = useMutation({
    mutationFn: async ({ 
      id, 
      reason 
    }: { 
      id: string; 
      reason?: string 
    }) => {
      const { error } = await supabase
        .from("interviews")
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          cancellation_reason: reason,
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interviews"] });
      toast({ title: "Interview cancelled" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to cancel interview",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const completeInterview = useMutation({
    mutationFn: async ({ 
      id, 
      feedback, 
      outcome 
    }: { 
      id: string; 
      feedback?: string; 
      outcome?: string 
    }) => {
      const { error } = await supabase
        .from("interviews")
        .update({
          status: "completed",
          feedback,
          outcome,
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interviews"] });
      toast({ title: "Interview marked as completed" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to complete interview",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    interviews,
    isLoading,
    refetch,
    updateInterview,
    cancelInterview,
    completeInterview,
  };
}
